/**
 * Shared wording for Staffing Requests, so the queue, the detail page and the form
 * describe a request the same way.
 */

import { type PositionDisciplines, positionLabel } from "@/lib/position-label";
import type { AddSeatOption, Composition, SeatRole } from "@/lib/staffing-change";
import type { StaffingRequestKind, StaffingRequestView } from "@/schemas/staffing-requests";

export const KIND_LABELS: Record<StaffingRequestKind, string> = {
  Add: "Add",
  Replace: "Replace",
  Remove: "Remove",
  Swap: "Swap",
};

export const KIND_DESCRIPTIONS: Record<StaffingRequestKind, string> = {
  Replace: "Take someone off the Pod and put someone else in their seat.",
  Add: "Fill an open seat, add a seat to the Pod, or add Supporting.",
  Remove: "Take someone off the Pod. Their seat stays open.",
  Swap: "Move someone from another Pod into a seat here. Their old seat opens up.",
};

/**
 * A seat's name: "R2 · SA" when the Solution knows what the seat is for, plain "R2" when it
 * doesn't, and "Supporting" for Supporting (which has no Discipline seat).
 */
export function seatLabel(role: SeatRole, disciplines?: PositionDisciplines | null): string {
  return positionLabel(role, disciplines);
}

export function addSeatLabel(
  option: AddSeatOption,
  disciplines?: PositionDisciplines | null,
): string {
  if (option.role === "Supporting") return "Supporting";
  const seat = seatLabel(option.role, disciplines);
  return option.expandsTo
    ? `${seat} — new seat (Pod grows to ${option.expandsTo})`
    : `${seat} — open seat`;
}

export function compositionLabel(composition: Composition): string {
  return `${composition} Pod`;
}

/**
 * One-line summary: "Replace Ana Ruiz (R1 · SA)", "Add to R3 · UX", "Remove Sam Lee
 * (Supporting)", "Swap Bo Lin into R2 · SA (from Portal R1)".
 */
export function summarizeRequest(request: StaffingRequestView): string {
  const seat = seatLabel(request.role, request.positionDisciplines);
  if (request.kind === "Add") return `Add to ${seat}`;
  if (request.kind === "Swap") {
    const who = request.suggestedResource?.name ?? "a former Resource";
    return `Swap ${who} into ${seat}${sourceSuffix(request)}`;
  }
  const who = request.target?.resource?.name ?? "a former Pod member";
  return `${KIND_LABELS[request.kind]} ${who} (${seat})`;
}

/** " (from Portal R1)" — where a Swap moves someone from; empty when that is unknown. */
export function sourceSuffix(request: StaffingRequestView): string {
  const from = sourceLabel(request);
  return from ? ` (from ${from})` : "";
}

/** "Portal R1" — the Solution and seat a Swap moves someone out of. */
export function sourceLabel(request: StaffingRequestView): string | null {
  const solution = request.source?.solution?.name;
  if (!solution) return null;
  const role = request.source?.role;
  return role ? `${solution} ${role}` : solution;
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** `needed_by` is a calendar date (YYYY-MM-DD) — format it without a timezone shift. */
export function formatCalendarDate(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
